import React, {createContext, PropsWithChildren, useContext, useEffect} from "react"
import useAsyncResultIdle from "../hooks/useAsyncResultIdle"
import {getPermissions, Permissions} from "../resources/permissions"
import {
	useAccessToken,
	useBankUserBankId,
	useIsAuthenticated,
	useIsBankUser,
	useIsOrgUser,
	useIsUnitSuperUser,
	useIsUnitUser,
} from "./auth"
import {claimsContext} from "./claims"
import {isBankManageResubmitApplication, isOrgManageResubmitApplication} from "../resources/orgPolicy"
import {Assignee} from "../resources/application"
import {isLocalEnv, isSandboxEnv} from "../utilities/environment"

const authContext = createContext<Map<string, [string]>>(new Map<string, [string]>())

export function PermissionProvider({children}: PropsWithChildren<{}>) {
	let accessToken: string | undefined = undefined
	const [getPermissionsState, getPermissionsFunc] = useAsyncResultIdle(getPermissions)

	useEffect(() => {
		if (accessToken !== undefined) {
			getPermissionsFunc(accessToken)
		}
	}, [accessToken, useIsAuthenticated()])

	function generatePermissions(t: Permissions) {
		const res = new Map<string, [string]>()
		t.attributes.permissions.map((p) => res.set(p.resource, p.actions))
		return res
	}

	if (useIsAuthenticated()) {
		accessToken = useAccessToken()
		return (
			<authContext.Provider
				value={getPermissionsState.match(
					() => new Map<string, [string]>(),
					() => new Map<string, [string]>(),
					(t) => generatePermissions(t),
					() => new Map<string, [string]>()
				)}
			>
				{children}
			</authContext.Provider>
		)
	} else {
		accessToken = undefined
		return <authContext.Provider value={new Map<string, [string]>()}>{children}</authContext.Provider>
	}
}

export function hasPermission(resource: string, action: string) {
	const permission = useContext(authContext)
	const resourceActions = permission.get(resource)
	if (resourceActions) {
		return resourceActions.includes(action)
	}
	return false
}

export function useIsAllowedToShowAssigneeFilter() {
	const isUnit = useIsUnitUser()
	const isBank = useIsBankUser()
	const isOrg = useIsOrgUser()
	const orgId = useContext(claimsContext)?.orgId

	if (isUnit || isBank) {
		return true
	} else if (isOrg) {
		return (
			!!orgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isOrgManageResubmitApplication(orgId)
		)
	}
	return false
}

export function useIsAllowedToEvaluate(
	relatedOrgId: string,
	forceAssigneeCheck: boolean,
	assignee?: Assignee
): boolean {
	const isUnit = useIsUnitUser()
	const isOrg = useIsOrgUser()
	const isBank = useIsBankUser()
	const claims = useContext(claimsContext)

	if (isUnit) {
		return true
	} else if (isOrg) {
		const orgId = claims?.orgId
		return (
			!!orgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isOrgManageResubmitApplication(orgId)
		)
	} else if (isBank) {
		const bankId = claims?.bankId
		return (
			!!bankId &&
			!!relatedOrgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isBankManageResubmitApplication(bankId, relatedOrgId) &&
			(!forceAssigneeCheck || assignee === Assignee.Bank)
		)
	}
	return false
}

export function useIsAllowResubmit(
	enableReevaluateApplication: boolean,
	relatedOrgId: string,
	assignee: Assignee
): boolean {
	return (
		enableReevaluateApplication &&
		hasPermission("application.resubmit", "create") &&
		useIsManageResubmitApplication(relatedOrgId, assignee)
	)
}

export function useIsManageResubmitApplication(relatedOrgId: string, assignee: Assignee): boolean {
	const bankId = useBankUserBankId()
	const isUnit = useIsUnitUser()
	const isOrg = useIsOrgUser()
	const isBank = useIsBankUser()

	return (
		isUnit || isOrg || (isBank && isBankManageResubmitApplication(bankId, relatedOrgId) && assignee === Assignee.Bank)
	)
}

export function useCanAccessUnifiedApplications() {
	return useIsUnitUser()
}

export function useCanAccessProductApplications() {
	const isSuperUser = useIsUnitSuperUser()
	if (isLocalEnv()) {
		return true
	}
	if (isSandboxEnv()) {
		return isSuperUser
	}
	return false
}
